/**
 * Effect Elements Creation
 * Creates DOM elements for visual effects
 */

import { logger } from '@/utils/logger.js';
import type { EffectElements } from './types.js';

/**
 * Find target container for effects
 */
export function findTargetContainer(): HTMLElement {
  // Priority: character-only-panel > demo-container > character-container > body
  const selectors = [
    '.character-only-panel',
    '.demo-container',
    '.character-container',
    '#tokchan-character'
  ];

  if (document.body.classList.contains('character-only-mode')) {
    const overlayRoot = document.querySelector('.bgm-overlay-root');
    if (overlayRoot) {
      return overlayRoot as HTMLElement;
    }
  }

  selectors.push('body');

  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element) {
      return element as HTMLElement;
    }
  }

  logger.warn('⚠️ No suitable container found, using body');
  return document.body;
}

/**
 * Create screen flash element
 */
export function createScreenFlash(effectElements: EffectElements): void {
  if (effectElements.screenFlash) {
    return;
  }

  const targetContainer = findTargetContainer();

  try {
    effectElements.screenFlash = document.createElement('div');
    effectElements.screenFlash.id = 'beat-screen-flash';
    effectElements.screenFlash.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle, rgba(255,255,255,1) 0%, transparent 70%);
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      mix-blend-mode: screen;
      border-radius: inherit;
    `;
    targetContainer.appendChild(effectElements.screenFlash);
  } catch (error) {
    logger.error('❌ Failed to create screen flash element:', error);
  }
}

/**
 * Create particle container
 */
export function createParticleContainer(effectElements: EffectElements): void {
  if (effectElements.particles) {
    return;
  }

  const targetContainer = findTargetContainer();

  try {
    effectElements.particles = document.createElement('div');
    effectElements.particles.id = 'beat-particles';
    effectElements.particles.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 999;
      overflow: visible;
      border-radius: inherit;
      background: transparent;
    `;
    targetContainer.appendChild(effectElements.particles);

    // Log container size after creation
    setTimeout(() => {
      if (effectElements.particles) {
        // Container created and sized
      }
    }, 100);
  } catch (error) {
    logger.error('❌ Failed to create particle container:', error);
  }
}

/**
 * Create background overlay
 */
export function createBackgroundOverlay(effectElements: EffectElements): void {
  if (effectElements.background) {
    return;
  }

  const targetContainer = findTargetContainer();

  try {
    effectElements.background = document.createElement('div');
    effectElements.background.id = 'beat-background-overlay';
    effectElements.background.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1;
      opacity: 0.3;
      background: linear-gradient(45deg, #1a237e, #3f51b5);
      transition: all 0.5s ease;
      mix-blend-mode: overlay;
      border-radius: inherit;
    `;
    targetContainer.appendChild(effectElements.background);
  } catch (error) {
    logger.error('❌ Failed to create background overlay:', error);
  }
}

/**
 * Create shockwave effect element
 */
export function createShockwaveEffect(effectElements: EffectElements): void {
  if (effectElements.shockwave) {
    return;
  }

  const targetContainer = findTargetContainer();

  try {
    effectElements.shockwave = document.createElement('div');
    effectElements.shockwave.id = 'beat-shockwave';
    effectElements.shockwave.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0px;
      height: 0px;
      border: 2px solid rgba(255, 255, 255, 0.8);
      border-radius: 50%;
      pointer-events: none;
      z-index: 998;
      transform: translate(-50%, -50%);
      opacity: 0;
    `;
    targetContainer.appendChild(effectElements.shockwave);
  } catch (error) {
    logger.error('❌ Failed to create shockwave element:', error);
  }
}

/**
 * Initialize all effect elements
 */
export function initializeEffectElements(effectElements: EffectElements): void {
  createScreenFlash(effectElements);
  createParticleContainer(effectElements);
  createBackgroundOverlay(effectElements);
  createShockwaveEffect(effectElements);
}

/**
 * Ensure element exists (lazy initialization)
 */
export function ensureElementExists(
  elementType: keyof EffectElements,
  effectElements: EffectElements,
  particlePool: HTMLElement[],
  _maxParticles: number,
  initializeParticleSystem: () => void
): void {
  switch(elementType) {
    case 'screenFlash':
      if (!effectElements.screenFlash) {
        createScreenFlash(effectElements);
      }
      break;
    case 'background':
      if (!effectElements.background) {
        createBackgroundOverlay(effectElements);
      }
      break;
    case 'particles':
      if (!effectElements.particles) {
        createParticleContainer(effectElements);
      }
      if (particlePool.length === 0) {
        initializeParticleSystem();
      }
      break;
    case 'shockwave':
      if (!effectElements.shockwave) {
        createShockwaveEffect(effectElements);
      }
      break;
  }
}
